
import { useState, useMemo } from 'react';
import { Product } from '../domain/types';

interface UseProductFilteringProps {
    products: Product[];
    initialCategory?: string;
    externalFilter?: string;
    externalSearch?: string;
    externalSort?: 'featured' | 'priceAsc' | 'priceDesc' | 'newest';
}

export const useProductFiltering = ({ 
    products, 
    initialCategory = 'Todos',
    externalFilter,
    externalSearch,
    externalSort
}: UseProductFilteringProps) => {
    const [internalFilter, setFilter] = useState(initialCategory);
    const [internalSearch, setSearchQuery] = useState('');
    const [internalSortBy, setSortBy] = useState<'featured' | 'priceAsc' | 'priceDesc' | 'newest'>('featured');

    // Use external state if provided, fallback to internal
    const filter = externalFilter !== undefined ? externalFilter : internalFilter;
    const searchQuery = externalSearch !== undefined ? externalSearch : internalSearch;
    const sortBy = externalSort !== undefined ? externalSort : internalSortBy;

    const displayedProducts = useMemo(() => {
        let result = [...products];
        
        // 1. Category Filter (Only if not already filtered by server)
        if (filter !== 'Todos') {
            result = result.filter(p => p.category === filter);
        }

        // 2. Search Filter (Only if not already filtered by server)
        if (searchQuery && searchQuery.length > 1) {
            const q = searchQuery.toLowerCase();
            result = result.filter(p => 
                p.title.toLowerCase().includes(q) || 
                p.category.toLowerCase().includes(q) ||
                (p.keywords && p.keywords.some(k => k.toLowerCase().includes(q)))
            );
        }

        // 3. Sorting (Always applied to ensure UI consistency)
        if (sortBy === 'priceAsc') result.sort((a, b) => a.price - b.price);
        else if (sortBy === 'priceDesc') result.sort((a, b) => b.price - a.price);
        else if (sortBy === 'newest') {
            result.sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return dateB - dateA;
            });
        }
        else result.sort((a, b) => (b.isFeatured === a.isFeatured ? 0 : b.isFeatured ? 1 : -1));
        
        return result;
    }, [products, filter, searchQuery, sortBy]);

    // Cross-category matching logic
    const crossCategoryMatches = useMemo(() => {
        if (displayedProducts.length === 0 && searchQuery && filter !== 'Todos') {
            const q = searchQuery.toLowerCase();
            return products.filter(p => 
                p.title.toLowerCase().includes(q) || 
                p.category.toLowerCase().includes(q) ||
                p.tag?.toLowerCase().includes(q) ||
                (p.keywords && p.keywords.some(k => k.includes(q)))
            );
        }
        return [];
    }, [displayedProducts.length, searchQuery, filter, products]);

    return {
        filter,
        setFilter,
        searchQuery,
        setSearchQuery,
        sortBy,
        setSortBy,
        displayedProducts,
        crossCategoryMatches
    };
};
